import React, { ChangeEvent } from 'react'

import { createId } from '@paralleldrive/cuid2'
import {
  IconUserPlus,
  IconCopy,
  IconInfoCircle,
  IconTrash,
  IconClock,
} from '@tabler/icons-react'
import { Menu, Space, Table, Tooltip } from 'antd'
import { times } from 'lodash'
import styled from 'styled-components'

import {
  calculateHours,
  calculateTotalHours,
  cloneValue,
} from '../WorkerAttendance/workerAttendanceHelpers'

import { Button } from '@/components/shared-components/BUI/Button'
import { Input } from '@/components/shared-components/BUI/Input'
import { Text } from '@/components/shared-components/BUI/Text'
import EllipsisDropdown from '@/components/shared-components/EllipsisDropdown'
import { AttendanceValue } from '@/modules/templates/types/blueprint'

import { SubcontractorsByProjectUiQuery } from 'types/api.graphql'

const TableFooter = styled.div`
  display: flex;
  align-items: center;
`

type Props = {
  value?: AttendanceValue
  onChange?: (value: AttendanceValue) => void
  projectSubcontractors: SubcontractorsByProjectUiQuery['subcontractorsByProject']
}

type InputChange = ChangeEvent<HTMLInputElement>

export const CaptureSubcontractorSummary: React.FC<Props> = ({
  value,
  onChange,
  projectSubcontractors,
}) => {
  const [attendanceList, setAttendanceList] = React.useState([])

  React.useEffect(() => {
    const allSubbies = projectSubcontractors.map((subbie) => ({
      comments: '',
      manuallyCreated: true,
      subcontractorId: subbie.id,
      workerAttendance: [],
    }))

    // Get all subcontractor ids that already have attendance
    const allExistingAttendanceIds = value?.subcontractorAttendances?.map(
      (attendance) => attendance.subcontractorId
    )

    // Filter out all subcontractors that already have attendance
    const allSubbiesWithoutExistingAttendance = allSubbies.filter(
      (attendance) =>
        !allExistingAttendanceIds?.includes(attendance.subcontractorId)
    )

    // Merge the existing attendance with the remaining subcontractors
    const mergedData = [
      ...value.subcontractorAttendances,
      ...allSubbiesWithoutExistingAttendance,
    ]

    setAttendanceList(mergedData)
  }, [projectSubcontractors, value?.subcontractorAttendances])

  const setManualWorkers = (index) => (e: InputChange) => {
    const newValue = cloneValue(value)
    const totalManualWorkers = e.target.value ? parseInt(e.target.value) : 0
    const manuallySetHours = attendanceList[index].workerAttendance?.[0]?.hours

    const updatedList = [
      ...attendanceList.slice(0, index),
      {
        ...attendanceList[index],
        workerAttendance: times(totalManualWorkers, () => ({
          hours: manuallySetHours,
          manuallyCreated: true,
        })),
      },
      ...attendanceList.slice(index + 1, attendanceList.length),
    ]

    newValue.subcontractorAttendances = updatedList

    onChange(newValue)
  }

  const setManualHours = (index) => (e: InputChange) => {
    const newValue = cloneValue(value)
    const manualHours = e.target.value ? parseFloat(e.target.value) : 0

    const updatedList = [
      ...attendanceList.slice(0, index),
      {
        ...attendanceList[index],
        workerAttendance: attendanceList[index].workerAttendance.map(
          (worker) => ({
            ...worker,
            hours: manualHours,
          })
        ),
      },
      ...attendanceList.slice(index + 1, attendanceList.length),
    ]

    newValue.subcontractorAttendances = updatedList

    setAttendanceList(updatedList)
    onChange(newValue)
  }

  const setComment = (index) => (e: InputChange) => {
    const newValue = cloneValue(value)
    const updatedList = [
      ...attendanceList.slice(0, index),
      {
        ...attendanceList[index],
        comments: e.target.value,
      },
      ...attendanceList.slice(index + 1, attendanceList.length),
    ]

    newValue.subcontractorAttendances = updatedList

    setAttendanceList(updatedList)
    onChange(newValue)
  }

  const duplicateManualAttendance = (index: number) => async () => {
    const newValue = cloneValue(value)

    const updatedList = [
      ...attendanceList.slice(0, index + 1),
      {
        ...attendanceList[index],
        comments: '',
        workerAttendance: [],
      },
      ...attendanceList.slice(index + 1, attendanceList.length),
    ]

    newValue.subcontractorAttendances = updatedList

    setAttendanceList(updatedList)
    onChange(newValue)
  }

  const extraMenu = (index: number) => (
    <Menu
      items={[
        {
          key: 'add',
          label: 'New entry for this subcontractor',
          icon: <IconCopy size={16} />,
          onClick: () => {
            duplicateManualAttendance(index)()
          },
        },
      ]}
    />
  )

  const addManualAttendance = () => {
    const newValue = cloneValue(value)

    const updatedList = [
      ...attendanceList,
      {
        comments: '',
        manuallyCreated: true,
        adHoc: true,
        subcontractorId: `adhoc-${createId()}`,
        workerAttendance: [],
      },
    ]

    newValue.subcontractorAttendances = updatedList

    setAttendanceList(updatedList)
    onChange(newValue)
  }

  const setSubcontractorName = (index) => (e: InputChange) => {
    const newValue = cloneValue(value)
    const updatedList = [
      ...attendanceList.slice(0, index),
      {
        ...attendanceList[index],
        subcontractorName: e.target.value,
      },
      ...attendanceList.slice(index + 1, attendanceList.length),
    ]

    newValue.subcontractorAttendances = updatedList

    setAttendanceList(updatedList)
    onChange(newValue)
  }

  const deleteManualAttendance = (index: number) => () => {
    const newValue = cloneValue(value)
    const updatedList = [
      ...attendanceList.slice(0, index),
      ...attendanceList.slice(index + 1, attendanceList.length),
    ]

    newValue.subcontractorAttendances = updatedList

    setAttendanceList(updatedList)
    onChange(newValue)
  }

  const getSubcontractorName = (
    attendance: AttendanceValue['subcontractorAttendances'][number]
  ) => {
    return projectSubcontractors.find(
      (subbie) => subbie.id === attendance.subcontractorId
    )?.company.name
  }

  const isIncomplete = (
    attendance: AttendanceValue['subcontractorAttendances'][number]
  ) => {
    const numberOfWorkers = attendance.workerAttendance?.length || 0
    const numberOfHours =
      attendance.workerAttendance?.reduce((_, worker) => worker.hours, 0) || 0

    return numberOfWorkers > 0 && numberOfHours === 0
  }

  return (
    <>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Table
          scroll={{ x: 'max-content' }}
          dataSource={attendanceList}
          pagination={false}
          rowKey={
            (attendance, index) => `${attendance.subcontractorId}-${index}` // TODO: Find a better way to set the row key (index here is deprecated)
          }
          footer={() => (
            <TableFooter>
              <Button
                variant="secondary-text"
                onClick={addManualAttendance}
                icon={<IconUserPlus size={16} />}
              >
                Add manual subcontractor
                <Tooltip title="Add a subcontractor that is not registered.">
                  <IconInfoCircle size={16} />
                </Tooltip>
              </Button>
              {value.subcontractorAttendances && (
                <Text
                  secondary="_600"
                  style={{
                    display: 'flex',
                    textAlign: 'right',
                    marginLeft: 'auto',
                  }}
                >
                  Total hours:{' '}
                  {calculateTotalHours(
                    value.subcontractorAttendances,
                    value.overrides
                  )}
                </Text>
              )}
            </TableFooter>
          )}
          columns={[
            {
              title: 'Companies',
              render: (_, attendance, index) =>
                attendance.adHoc ? (
                  <Input
                    type="subcontractorName"
                    placeholder="Add a name"
                    onChange={setSubcontractorName(index)}
                    value={attendance.subcontractorName}
                  />
                ) : (
                  getSubcontractorName(attendance)
                ),
              width: 220,
            },
            {
              title: 'Workers',
              render: (_, attendance, index) => {
                return (
                  <Input
                    type="number"
                    min={0}
                    placeholder="0"
                    onChange={setManualWorkers(index)}
                    value={attendance.workerAttendance?.length || 0}
                  />
                )
              },
              width: 100,
            },
            {
              title: 'Hours',
              width: 120,
              render: (_, attendance, index) => (
                <Input
                  status={isIncomplete(attendance) && 'warning'}
                  prefix={
                    isIncomplete(attendance) && (
                      <Tooltip title="No hours assigned for this subcontractor">
                        <IconClock size={14} />
                      </Tooltip>
                    )
                  }
                  type="number"
                  min={0}
                  step={0.5}
                  placeholder="0"
                  onChange={setManualHours(index)}
                  value={
                    attendance.workerAttendance?.reduce(
                      (_, worker) => worker.hours,
                      0
                    ) || 0
                  }
                />
              ),
            },
            {
              title: 'Total',
              width: 80,
              render: (_, attendance) => {
                return <>{attendance ? calculateHours(attendance, []) : 0}</>
              },
            },
            {
              title: 'Comments',
              render: (_, attendance, index) => {
                return (
                  <Input
                    placeholder="Add a comment"
                    value={attendance.comments || ''}
                    onChange={setComment(index)}
                  />
                )
              },
            },
            {
              title: '',
              render: (_, _attendance, index) => {
                return _attendance.adHoc ? (
                  <Button
                    variant="secondary-text"
                    onClick={deleteManualAttendance(index)}
                  >
                    <IconTrash size={16} />
                  </Button>
                ) : (
                  <EllipsisDropdown menu={extraMenu(index)} />
                )
              },
              width: 40,
            },
          ]}
        ></Table>
      </Space>
    </>
  )
}
